import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TarotCard from '../components/TarotCard';
import SectionHeading from '../components/SectionHeading';
import { Toast, useToast } from '../components/Toast';
import { ApiError, tarotApi, type ReportResponse } from '../lib/api';
import { renderSimpleMarkdown } from '../lib/markdown';
import { ThemeBackdrop } from '../lib/themeEmblem';
import { useTheme } from '../lib/useTheme';

/**
 * /session/:id/report — v2 REPLACE per spec §5.
 *
 * 版面節奏（editorial，不是 SaaS dashboard）：
 *   kicker → 章標 → 引文段 → 三張牌（小尺寸）→ Shadow Work →
 *   6-month Arc → Journaling Prompts → Ritual → Closing (italic)
 *
 * - 用 <SectionHeading> 元件（另一個 sub-agent 建的；export 介面假設為 default）
 * - PDF preview iframe 保留
 * - Closing 段用 italic
 * - 不寫死英文 — 全 i18n
 */

interface ArcMonth {
  month: string;
  label: string;
  note: string;
}

const DEMO_REPORT: ReportResponse = {
  generated_at: Math.floor(Date.now() / 1000),
  summary_markdown: 'demo.report.summary',
  cards_summary: [
    {
      slug: 'the-tower',
      position: 'past',
      interpretation: 'demo.report.cards.tower',
    },
    {
      slug: 'the-hermit',
      position: 'present',
      interpretation: 'demo.report.cards.hermit',
    },
    {
      slug: 'the-star',
      position: 'future',
      interpretation: 'demo.report.cards.star',
    },
  ],
  key_insights: [
    'demo.report.prompts.0',
    'demo.report.prompts.1',
    'demo.report.prompts.2',
    'demo.report.prompts.3',
  ],
  pdf_url: '/non-existent-demo.pdf',
};

export default function Report() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const useMockReport = searchParams.get('mock') === '1';

  const [report, setReport] = useState<ReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfFailed, setPdfFailed] = useState(false);
  const [paymentRequired, setPaymentRequired] = useState(false);
  const [toast, showToast] = useToast();

  useEffect(() => {
    if (!id) return;
    let alive = true;

    (async () => {
      try {
        const data = await tarotApi.getReport(id, { mock: useMockReport });
        if (!alive) return;
        setReport(data);
      } catch (e) {
        if (!alive) return;
        if (e instanceof ApiError && e.status === 402) {
          setPaymentRequired(true);
        } else {
          console.warn('[report] using demo fallback', e);
          setReport(DEMO_REPORT);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id, useMockReport]);

  function handleEmail() {
    showToast(t('report.email.sent'));
  }

  function isMockPdf(url: string): boolean {
    return url.endsWith('/non-existent-demo.pdf') || url.includes('mock-sample');
  }

  if (loading) {
    return (
      <main className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center px-6">
        <p className="text-sm italic text-muted">{t('report.loading')}</p>
      </main>
    );
  }

  if (paymentRequired) {
    return (
      <main className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center px-6 text-center">
        <h1 className="font-display text-2xl text-heading">
          {t('report.errors.paymentRequired')}
        </h1>
        <Link
          to={`/session/${id}/paywall`}
          className="mt-6 px-5 py-2 text-sm transition-opacity hover:opacity-90"
          style={{
            background: 'var(--btn-bg)',
            color: 'var(--btn-ink)',
            borderRadius: 'var(--radius-pill)',
          }}
        >
          {t('report.errors.goPay')}
        </Link>
      </main>
    );
  }

  if (!report) {
    return (
      <main className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center px-6 text-center">
        <p className="text-sm italic text-muted">{t('report.errors.notFound')}</p>
        <Link
          to="/onboarding"
          className="mt-6 px-5 py-2 text-sm transition-opacity hover:opacity-90"
          style={{
            background: 'var(--btn-bg)',
            color: 'var(--btn-ink)',
            borderRadius: 'var(--radius-pill)',
          }}
        >
          {t('report.cta.newReading')}
        </Link>
      </main>
    );
  }

  const generatedAt = new Date(report.generated_at * 1000);
  const isDemoMd = report.summary_markdown.startsWith('demo.');
  const summaryMd = isDemoMd ? t(report.summary_markdown) : report.summary_markdown;

  // 6-month arc — i18n key list（demo 走 i18n；正版 backend 也應走 same shape，未來 ReportResponse 加 arc 欄位即可）
  const arc = (t('report.arc.items', { returnObjects: true }) as ArcMonth[]) || [];

  // Journaling prompts — 從 key_insights 拉，但 demo 模式下要 t() 解析
  const prompts = report.key_insights.map((p) => (p.startsWith('demo.') ? t(p) : p));

  // Ritual / Closing 文案
  const ritualBody = t('report.ritual.body');
  const closingBody = t('report.closing.body');

  return (
    <main className="relative isolate min-h-screen">
      <ThemeBackdrop theme={theme} />

      <article data-testid="report-page" className="relative z-10 mx-auto w-full max-w-3xl px-6 pb-24 pt-24 sm:pt-28">
        <Link to="/" className="mb-10 inline-block text-sm text-muted hover:text-fg">
          ← {t('common.nav.back')}
        </Link>

        {/* Editorial header */}
        <header className="mb-12">
          <p className="mb-3 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-accent">
            {t('report.kicker')}
          </p>
          <h1
            className="text-balance font-display text-4xl leading-[1.1] text-heading sm:text-5xl"
            style={{ letterSpacing: 'var(--h1-tracking)' }}
          >
            {t('report.title')}
          </h1>
          <p className="mt-3 font-mono text-[0.65rem] uppercase tracking-[0.18em] text-muted">
            {t('report.generatedAt', { date: generatedAt.toLocaleDateString() })}
          </p>
        </header>

        {/* Lede — 主章節引文段 */}
        <section
          className="mb-14 px-2 sm:px-0"
          aria-label={t('report.ledeAria')}
        >
          <p className="text-pretty font-display text-xl leading-relaxed text-fg sm:text-2xl">
            {t('report.lede')}
          </p>
        </section>

        {/* Summary markdown */}
        <section data-testid="report-summary" className="mb-16 space-y-4">
          {renderSimpleMarkdown(summaryMd)}
        </section>

        {/* Three-card spread (small) */}
        <section className="mb-16">
          <SectionHeading kicker={t('report.cards.kicker')} title={t('report.cardsHeading')} level="lg" />
          <div className="mt-6 grid gap-6 sm:grid-cols-3">
            {report.cards_summary.map((c, i) => {
              const interp = c.interpretation.startsWith('demo.')
                ? t(c.interpretation)
                : c.interpretation;
              return (
                <div key={`${c.slug}-${i}`} data-testid={`report-card-${i}`} className="flex flex-col items-center gap-3">
                  <TarotCard
                    slug={c.slug}
                    theme={theme}
                    revealed
                    delay={200 + i * 350}
                    position={t(`report.positions.${c.position}`, c.position)}
                    className="w-full max-w-[140px]"
                  />
                  <div className="text-pretty text-center text-sm leading-relaxed text-muted">
                    {renderSimpleMarkdown(interp)}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Shadow Work */}
        <section className="mb-16">
          <SectionHeading kicker={t('report.shadow.kicker')} title={t('report.shadow.title')} level="lg" />
          <p className="mt-4 text-pretty text-base leading-relaxed text-fg">
            {t('report.shadow.body')}
          </p>
        </section>

        {/* 6-month Arc */}
        <section className="mb-16">
          <SectionHeading kicker={t('report.arc.kicker')} title={t('report.arc.title')} level="lg" />
          <ol className="mt-6">
            {arc.map((m, i) => (
              <li
                key={i}
                className={[
                  'flex gap-5 py-4',
                  i < arc.length - 1 ? 'border-b border-border' : '',
                ].join(' ')}
              >
                <div
                  className="font-mono text-[0.65rem] uppercase tracking-[0.18em] text-muted"
                  style={{ minWidth: 64, paddingTop: 4 }}
                >
                  {m.month}
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-lg text-heading">{m.label}</h3>
                  <p className="mt-1 text-pretty text-sm leading-relaxed text-muted">
                    {m.note}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* Journaling Prompts */}
        <section className="mb-16">
          <SectionHeading kicker={t('report.prompts.kicker')} title={t('report.prompts.title')} level="lg" />
          <ol className="mt-6 space-y-1">
            {prompts.map((q, i) => (
              <li
                key={i}
                className={[
                  'flex gap-5 py-4',
                  i < prompts.length - 1 ? 'border-b border-border' : '',
                ].join(' ')}
              >
                <span
                  className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-accent"
                  style={{ minWidth: 28, paddingTop: 4 }}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="text-pretty text-base leading-relaxed text-fg">
                  {renderSimpleMarkdown(q)}
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* Ritual */}
        <section className="mb-16">
          <SectionHeading kicker={t('report.ritual.kicker')} title={t('report.ritual.title')} level="lg" />
          <p className="mt-4 text-pretty text-base leading-relaxed text-fg">{ritualBody}</p>
        </section>

        {/* Closing — italic */}
        <section className="mb-16">
          <SectionHeading kicker={t('report.closing.kicker')} title={t('report.closing.title')} level="lg" />
          <p className="mt-4 text-pretty font-display text-lg italic leading-relaxed text-fg sm:text-xl">
            {closingBody}
          </p>
        </section>

        {/* PDF preview */}
        <section className="mb-12">
          <SectionHeading kicker={t('report.pdf.kicker')} title={t('report.pdfHeading')} level="lg" />
          {pdfFailed || isMockPdf(report.pdf_url) ? (
            <div
              className="mt-6 border border-dashed border-border bg-surface p-8 text-center text-sm text-muted"
              style={{ borderRadius: 'var(--radius-card)' }}
            >
              <p className="font-display text-base text-heading">
                {t('report.pdfComingSoon')}
              </p>
              <p className="mt-1 text-xs">{t('report.pdfComingSoonHint')}</p>
            </div>
          ) : (
            <div
              className="mt-6 overflow-hidden border border-border bg-surface"
              style={{ borderRadius: 'var(--radius-card)' }}
            >
              <iframe
                title={t('report.pdfHeading')}
                src={report.pdf_url}
                onError={() => setPdfFailed(true)}
                onLoad={(e) => {
                  const iframe = e.currentTarget;
                  try {
                    if (!iframe.src || isMockPdf(iframe.src)) {
                      setPdfFailed(true);
                    }
                  } catch {
                    setPdfFailed(true);
                  }
                }}
                className="h-96 w-full"
              />
            </div>
          )}
        </section>

        {/* CTAs */}
        <section className="flex flex-wrap gap-3 border-t border-border pt-10">
          <button
            data-testid="report-email"
            type="button"
            onClick={handleEmail}
            className="px-6 py-2.5 text-sm font-medium transition-opacity hover:opacity-90"
            style={{
              background: 'var(--btn-bg)',
              color: 'var(--btn-ink)',
              border: '1px solid var(--btn-border, transparent)',
              borderRadius: 'var(--radius-pill)',
              boxShadow: 'var(--btn-shadow)',
            }}
          >
            {t('report.cta.email')}
          </button>
          <Link
            to="/onboarding"
            className="rounded-pill border border-border px-6 py-2.5 text-sm transition-colors hover:bg-elevated"
          >
            {t('report.cta.newReading')}
          </Link>
        </section>

        <Toast state={toast} />
      </article>
    </main>
  );
}
